import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Package,
  Upload,
  X,
} from "lucide-react";

interface StoreItem {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  images: string[];
  isPublished: boolean;
  stock: number;
  tags: string[];
}

interface StoreManagementProps {
  items: StoreItem[];
  onItemsUpdate: (items: StoreItem[]) => void;
}

const StoreManagement = ({ items, onItemsUpdate }: StoreManagementProps) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<StoreItem | null>(null);
  const [newItem, setNewItem] = useState<Partial<StoreItem>>({
    name: "",
    description: "",
    price: 0,
    category: "Kente Sash",
    images: [],
    isPublished: false,
    stock: 1,
    tags: [],
  });

  const categories = [
    "Kente Sash",
    "Kente Bag",
    "Kente Smock",
    "Kente Tie",
    "Kente Scarf",
    "Kente Accessories",
  ];

  const handleAddItem = () => {
    if (!newItem.name || !newItem.description || newItem.price === 0) {
      alert("Please fill in all required fields");
      return;
    }

    const item: StoreItem = {
      id: Date.now().toString(),
      name: newItem.name!,
      description: newItem.description!,
      price: newItem.price!,
      category: newItem.category!,
      images: newItem.images || [],
      isPublished: newItem.isPublished || false,
      stock: newItem.stock || 1,
      tags: newItem.tags || [],
    };

    onItemsUpdate([...items, item]);
    setNewItem({
      name: "",
      description: "",
      price: 0,
      category: "Kente Sash",
      images: [],
      isPublished: false,
      stock: 1,
      tags: [],
    });
    setIsAddDialogOpen(false);
  };

  const handleEditItem = (item: StoreItem) => {
    setEditingItem(item);
    setNewItem(item);
    setIsAddDialogOpen(true);
  };

  const handleUpdateItem = () => {
    if (
      !editingItem ||
      !newItem.name ||
      !newItem.description ||
      newItem.price === 0
    ) {
      alert("Please fill in all required fields");
      return;
    }

    const updatedItems = items.map((item) =>
      item.id === editingItem.id ? { ...item, ...newItem } : item
    );

    onItemsUpdate(updatedItems);
    setEditingItem(null);
    setNewItem({
      name: "",
      description: "",
      price: 0,
      category: "Kente Sash",
      images: [],
      isPublished: false,
      stock: 1,
      tags: [],
    });
    setIsAddDialogOpen(false);
  };

  const handleDeleteItem = (itemId: string) => {
    if (confirm("Are you sure you want to delete this item?")) {
      onItemsUpdate(items.filter((item) => item.id !== itemId));
    }
  };

  const handleTogglePublished = (itemId: string) => {
    const updatedItems = items.map((item) =>
      item.id === itemId ? { ...item, isPublished: !item.isPublished } : item
    );
    onItemsUpdate(updatedItems);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const imageUrls = Array.from(files).map((file) =>
        URL.createObjectURL(file)
      );
      setNewItem((prev) => ({
        ...prev,
        images: [...(prev.images || []), ...imageUrls],
      }));
    }
  };

  const removeImage = (index: number) => {
    setNewItem((prev) => ({
      ...prev,
      images: prev.images?.filter((_, i) => i !== index) || [],
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold">Store Items</h3>
          <p className="text-muted-foreground">
            Manage your Kente products and inventory
          </p>
        </div>

        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? "Edit Item" : "Add New Item"}
              </DialogTitle>
              <DialogDescription>
                {editingItem
                  ? "Update your item details"
                  : "Add a new Kente product to your store"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="item-name">Item Name *</Label>
                  <Input
                    id="item-name"
                    value={newItem.name}
                    onChange={(e) =>
                      setNewItem((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="e.g., Traditional Kente Sash"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="item-category">Category *</Label>
                  <select
                    id="item-category"
                    value={newItem.category}
                    onChange={(e) =>
                      setNewItem((prev) => ({
                        ...prev,
                        category: e.target.value,
                      }))
                    }
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="item-description">Description *</Label>
                <Textarea
                  id="item-description"
                  value={newItem.description}
                  onChange={(e) =>
                    setNewItem((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Describe your Kente item..."
                  rows={3}
                />
              </div>

              {/* Price and Stock */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="item-price">Price (₵) *</Label>
                  <Input
                    id="item-price"
                    type="number"
                    value={newItem.price}
                    onChange={(e) =>
                      setNewItem((prev) => ({
                        ...prev,
                        price: Number(e.target.value),
                      }))
                    }
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="item-stock">Stock Quantity</Label>
                  <Input
                    id="item-stock"
                    type="number"
                    value={newItem.stock}
                    onChange={(e) =>
                      setNewItem((prev) => ({
                        ...prev,
                        stock: Number(e.target.value),
                      }))
                    }
                    placeholder="1"
                    min="0"
                  />
                </div>
              </div>

              {/* Images */}
              <div className="space-y-2">
                <Label>Product Images</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className="flex flex-col items-center justify-center cursor-pointer"
                  >
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">
                      Click to upload images or drag and drop
                    </span>
                  </label>
                </div>

                {/* Image Preview */}
                {newItem.images && newItem.images.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {newItem.images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-20 object-cover rounded"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute -top-2 -right-2 h-6 w-6 p-0"
                          onClick={() => removeImage(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Published Status */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is-published"
                  checked={newItem.isPublished}
                  onChange={(e) =>
                    setNewItem((prev) => ({
                      ...prev,
                      isPublished: e.target.checked,
                    }))
                  }
                  className="rounded"
                />
                <Label htmlFor="is-published">Publish this item</Label>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddDialogOpen(false);
                  setEditingItem(null);
                  setNewItem({
                    name: "",
                    description: "",
                    price: 0,
                    category: "Kente Sash",
                    images: [],
                    isPublished: false,
                    stock: 1,
                    tags: [],
                  });
                }}
              >
                Cancel
              </Button>
              <Button onClick={editingItem ? handleUpdateItem : handleAddItem}>
                {editingItem ? "Update Item" : "Add Item"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Items Grid */}
      {items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No items yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Start building your store by adding your first Kente item
            </p>
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Item
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <Card key={item.id} className="overflow-hidden">
              <div className="aspect-video bg-gray-100 relative">
                {item.images.length > 0 ? (
                  <img
                    src={item.images[0]}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <Package className="h-12 w-12 text-gray-400" />
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <Badge variant={item.isPublished ? "default" : "secondary"}>
                    {item.isPublished ? "Published" : "Draft"}
                  </Badge>
                </div>
              </div>

              <CardContent className="p-4">
                <div className="space-y-2">
                  <h4 className="font-semibold truncate">{item.name}</h4>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {item.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-lg">₵{item.price}</span>
                    <span className="text-sm text-muted-foreground">
                      Stock: {item.stock}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditItem(item)}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleTogglePublished(item.id)}
                    >
                      {item.isPublished ? (
                        <EyeOff className="h-3 w-3 mr-1" />
                      ) : (
                        <Eye className="h-3 w-3 mr-1" />
                      )}
                      {item.isPublished ? "Unpublish" : "Publish"}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default StoreManagement;
